// The portal. /<client>: their projects. /<client>/<project>: one project,
// /estimate for its estimate tab. Not logged in: the password bar.
import * as React from "react"
import { followMove, loadProjects, logout, type Loaded } from "./api"
import { Header } from "./Header"
import { Login } from "./Login"
import { Menu } from "./Menu"
import { Overview } from "./Overview"
import { ProjectPage } from "./Project"
import { go, useRoute } from "./route"
import { Studio } from "./studio/Studio"
import { setLang, t, useLang } from "./i18n"

type State = { kind: "loading" } | { kind: "login" } | { kind: "in"; data: Loaded } | { kind: "error" } | { kind: "nolink" }

export function App() {
    // the studio (Tiago's side) lives at /admin
    return location.pathname.split("/")[1]?.toLowerCase() === "admin" ? <Studio /> : <ClientApp />
}

function ClientApp() {
    useLang() // (everything below re-renders when the language changes)
    const route = useRoute()
    const client = route.client
    const [state, setState] = React.useState<State>(client ? { kind: "loading" } : { kind: "nolink" })
    const [fromLogin, setFromLogin] = React.useState(false)

    const load = React.useCallback(async () => {
        try {
            const data = await loadProjects(client)
            if (data) setLang(data.lang)
            setState(data ? { kind: "in", data } : { kind: "login" })
        } catch {
            if (await followMove(client)) return
            setState({ kind: "error" })
        }
    }, [client])

    React.useEffect(() => {
        if (client) load()
    }, [client, load])

    // a client with one project lands straight on it, once, on arrival;
    // "All projects" still opens the overview afterwards
    const data = state.kind === "in" ? state.data : null
    const landed = React.useRef(false)
    React.useEffect(() => {
        if (!data || landed.current) return
        landed.current = true
        if (!route.project && data.projects.length === 1)
            go({ client, project: data.projects[0].project.id, tab: "planning" }, { replace: true, quiet: true })
    }, [data])

    const entry = data && route.project ? data.projects.find((e) => e.project.id === route.project) : undefined

    React.useEffect(() => {
        document.title = entry ? `${entry.project.title} · NTO` : data ? `${data.name} · NTO` : "NTO · Client"
    }, [entry, data])

    // after the first page, entrances start sooner
    const first = React.useRef(true)
    const delay = first.current ? 250 : 60
    React.useEffect(() => {
        if (state.kind === "in") first.current = false
    })

    return (
        <div className="p-root" data-state={state.kind}>
            <Header home={client ? "/" + client : undefined} />
            {state.kind === "login" && (
                <Login
                    client={client}
                    onIn={() => {
                        setFromLogin(true)
                        load()
                    }}
                />
            )}
            {data && (
                <>
                    {entry ? (
                        <ProjectPage key={entry.project.id} client={client} clientName={data.name} logo={data.logo} entry={entry} route={route} fromLogin={fromLogin} onReload={load} />
                    ) : route.project ? (
                        <p className="p-quiet nto-mono">{t("There's no project here. Open the menu to see yours.")}</p>
                    ) : (
                        <Overview key="overview" data={data} route={route} delay={fromLogin ? 80 : delay} />
                    )}
                    <Menu
                        data={data}
                        route={route}
                        onLogout={async () => {
                            await logout()
                            setFromLogin(false)
                            setState({ kind: "login" })
                            history.replaceState(null, "", "/" + client)
                        }}
                    />
                </>
            )}
            {state.kind === "nolink" && <p className="p-quiet nto-mono">{t("Open the link you received to see your projects.")}</p>}
            {state.kind === "error" && <p className="p-quiet nto-mono">{t("Something went wrong. Try again in a moment.")}</p>}
        </div>
    )
}
